import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from '../../components/common/Badge/Badge';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import DataTable from '../../components/common/DataTable/DataTable';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FilterChipsBar, { type FilterChipEntry } from '../../components/common/FilterChipsBar/FilterChipsBar';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import ListingBottomRow from '../../components/common/ListingBottomRow/ListingBottomRow';
import Loading from '../../components/common/Loading/Loading';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import SingleSelectFilter from '../../components/common/SingleSelectFilter/SingleSelectFilter';
import {
  PAYMENTS_DUE_OVERDUE_DAY_OPTIONS,
  formatPaymentsDueDateLabel,
  formatPaymentsDueOverdueLabel,
  type PaymentsDueOverdueDays,
} from '../../constants/datePresets';
import { usePaymentsDueDashboard } from '../../hooks/usePaymentsDueDashboard';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import { PAYMENT_SYSTEM_LABELS, type PaymentSystem } from '../../types/payment';
import { formatDateTime } from '../../utils';
import {
  formatPaymentDueAmount,
  paymentsDueStatusBadgeVariant,
  paymentsDueStatusLabel,
  resolvePaymentsDueQuery,
  resolvePaymentsDueStatus,
} from '../../utils/paymentsDueDashboard';
import dashboardStyles from './Dashboard.module.css';
import styles from './PaymentsDuePanel.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const GATEWAY_FILTER_VALUES = ['asaas', 'stripe', 'paypal', 'pix'] as const;
type PaymentsDueGateway = (typeof GATEWAY_FILTER_VALUES)[number];

const OVERDUE_DAY_VALUES = PAYMENTS_DUE_OVERDUE_DAY_OPTIONS.map(
  (option) => option.value,
) as PaymentsDueOverdueDays[];
const isOverdueDays = (value: string): value is PaymentsDueOverdueDays => (
  OVERDUE_DAY_VALUES.includes(value as PaymentsDueOverdueDays)
);

const HEADERS = ['Aluna', 'Plano', 'Último pagamento', 'Gateway', 'Valor', 'Status'];
const GRID_TEMPLATE = 'minmax(180px, 1.4fr) minmax(140px, 1fr) 160px 140px 120px minmax(150px, 1fr)';
const DEFAULT_GATEWAY: PaymentsDueGateway[] = ['pix'];

const PAYMENTS_DUE_FILTER_SCHEMA = {
  searchName: queryFilter.string(''),
  paymentGateway: queryFilter.array<PaymentsDueGateway>(DEFAULT_GATEWAY, {
    allowed: GATEWAY_FILTER_VALUES,
    emptyToken: 'all',
  }),
  overdueMinDays: queryFilter.enum<PaymentsDueOverdueDays>('', OVERDUE_DAY_VALUES, {
    param: 'diasVencidos',
  }),
  dueDate: queryFilter.string('', { param: 'vencimento', trim: true }),
  page: queryFilter.integer(1, { min: 1 }),
  pageSize: queryFilter.integer(20, { allowed: PAGE_SIZE_OPTIONS }),
};

const isPaymentsDueGateway = (value: string): value is PaymentsDueGateway => (
  (GATEWAY_FILTER_VALUES as readonly string[]).includes(value)
);

const gatewayLabel = (value: string | null): string => {
  if (!value) {
    return '—';
  }

  if (Object.prototype.hasOwnProperty.call(PAYMENT_SYSTEM_LABELS, value)) {
    return PAYMENT_SYSTEM_LABELS[value as PaymentSystem];
  }

  return value;
};

const areGatewaysEqual = (left: readonly string[], right: readonly string[]): boolean => (
  left.length === right.length && left.every((value, index) => value === right[index])
);

const PaymentsDuePanel: React.FC = () => {
  const navigate = useNavigate();
  const { filters, setFilter, resetFilters } = useQueryFilters(
    PAYMENTS_DUE_FILTER_SCHEMA,
    { pageKey: 'page' },
  );

  const resolved = useMemo(
    () => resolvePaymentsDueQuery(
      filters.searchName,
      filters.paymentGateway,
      filters.dueDate,
      filters.overdueMinDays,
      filters.page,
      filters.pageSize,
    ),
    [
      filters.dueDate,
      filters.overdueMinDays,
      filters.page,
      filters.pageSize,
      filters.paymentGateway,
      filters.searchName,
    ],
  );
  const query = resolved.ok ? resolved.query : null;
  const { snapshot, loading, error } = usePaymentsDueDashboard(query);
  const totalCount = snapshot?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / filters.pageSize));
  const page = Math.min(filters.page, totalPages);
  const students = snapshot?.students ?? [];
  const hasDueFilter = Boolean(filters.dueDate);
  const hasOverdueFilter = Boolean(filters.overdueMinDays);
  const hasActiveFilters = Boolean(filters.searchName.trim())
    || !areGatewaysEqual(filters.paymentGateway, DEFAULT_GATEWAY)
    || hasDueFilter
    || hasOverdueFilter;

  const gatewayOptions = useMemo(() => GATEWAY_FILTER_VALUES.map((value) => ({
    value,
    label: PAYMENT_SYSTEM_LABELS[value],
  })), []);

  const overdueOptions = useMemo(
    () => PAYMENTS_DUE_OVERDUE_DAY_OPTIONS.map((option) => ({
      value: option.value,
      label: option.label,
    })),
    [],
  );

  const chips: FilterChipEntry[] = useMemo(() => {
    const entries: FilterChipEntry[] = filters.paymentGateway.map((value) => ({
      filterKey: 'paymentGateway',
      value,
      label: PAYMENT_SYSTEM_LABELS[value],
      onRemove: () => setFilter(
        'paymentGateway',
        filters.paymentGateway.filter((item) => item !== value),
      ),
    }));

    if (hasOverdueFilter) {
      entries.push({
        filterKey: 'overdueMinDays',
        value: filters.overdueMinDays,
        label: `Dias vencidos: ${formatPaymentsDueOverdueLabel(filters.overdueMinDays)}`,
        onRemove: () => setFilter('overdueMinDays', ''),
      });
    }

    if (hasDueFilter) {
      entries.push({
        filterKey: 'dueDate',
        value: filters.dueDate,
        label: `Data vencimento: ${formatPaymentsDueDateLabel(filters.dueDate)}`,
        onRemove: () => setFilter('dueDate', ''),
      });
    }

    return entries;
  }, [
    filters.dueDate,
    filters.overdueMinDays,
    filters.paymentGateway,
    hasDueFilter,
    hasOverdueFilter,
    setFilter,
  ]);

  return (
    <section className={styles.panel}>
      <FilterBar gap="sm" compact pushFirstChild className={dashboardStyles.filtersRow}>
        <RecordCount label="A vencer/vencidos:" count={totalCount} />
        <FilterGroup>
          <SearchFilterBar
            searchValue={filters.searchName}
            onSearchChange={(value) => setFilter('searchName', value)}
            searchPlaceholder="Nome ou e-mail (aluna ou pagador)"
            loading={loading}
          />
        </FilterGroup>
        <FilterGroup>
          <SingleSelectFilter
            value={filters.overdueMinDays}
            onChange={(value) => setFilter('overdueMinDays', isOverdueDays(value) ? value : '')}
            options={overdueOptions}
            placeholder="Dias vencidos"
            neutralValue=""
            keepPlaceholder
          />
        </FilterGroup>
        <FilterGroup>
          <label className={styles['due-date-filter']}>
            <span className={styles['due-date-label']}>Data vencimento</span>
            <input
              type="date"
              className={styles['due-date-input']}
              value={filters.dueDate}
              onChange={(event) => setFilter('dueDate', event.target.value)}
              aria-label="Data vencimento"
            />
          </label>
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            values={filters.paymentGateway}
            onChange={(values) => setFilter('paymentGateway', values.filter(isPaymentsDueGateway))}
            options={gatewayOptions}
            placeholder="Gateway"
            clearButtonLabel="Limpar gateway"
            hideChips
          />
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={() => resetFilters()} />
        )}
      </FilterBar>

      <FilterChipsBar chips={chips} compact />

      {!resolved.ok && <EmptyState message={resolved.message} showImage={false} />}
      {resolved.ok && loading && !snapshot && <Loading />}
      {resolved.ok && !loading && error && <EmptyState message={error} showImage={false} />}
      {resolved.ok && !loading && !error && snapshot && totalCount === 0 && (
        <EmptyState
          message={hasActiveFilters
            ? 'Nenhuma aluna encontrada com os filtros selecionados.'
            : 'Nenhuma aluna a vencer neste critério.'}
          showImage={false}
        />
      )}
      {resolved.ok && !error && snapshot && totalCount > 0 && (
        <>
          <DataTable
            headers={HEADERS}
            gridTemplate={GRID_TEMPLATE}
            lastColumnIsActions={false}
            borderedRows
            boldHeaders
          >
            {students.map((row) => {
              const status = resolvePaymentsDueStatus(
                row.daysSincePaid,
                row.isSinglePayment,
                row.cycleDays,
              );
              return (
                <DataTable.Row key={`${row.studentId}-${row.paymentHistoryId ?? 'none'}`}>
                  <div className={styles['student-cell']}>
                    <button
                      type="button"
                      className={styles['name-link']}
                      onClick={() => navigate(`/alunas/${row.studentId}/editar?section=payments`)}
                    >
                      {row.studentName || '—'}
                    </button>
                    {row.studentEmail ? (
                      <span className={styles['student-email']}>{row.studentEmail}</span>
                    ) : null}
                  </div>
                  <div>{row.planName || '—'}</div>
                  <div className={styles.nowrap}>
                    {row.lastPaidAt ? formatDateTime(row.lastPaidAt) : 'Sem pagamento registrado'}
                  </div>
                  <div>{gatewayLabel(row.paymentSystem)}</div>
                  <div className={styles.nowrap}>
                    {formatPaymentDueAmount(row.value, row.currency)}
                  </div>
                  <div>
                    <Badge variant={paymentsDueStatusBadgeVariant(status)} solid>
                      {paymentsDueStatusLabel(
                        status,
                        row.daysSincePaid,
                        row.isSinglePayment,
                        row.cycleDays,
                      )}
                    </Badge>
                  </div>
                </DataTable.Row>
              );
            })}
          </DataTable>
          <ListingBottomRow
            pageSize={filters.pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={(event) => {
              const value = Number.parseInt(event.target.value, 10);
              if (PAGE_SIZE_OPTIONS.some((option) => option === value)) {
                setFilter('pageSize', value);
              }
            }}
            pagination={{
              page,
              pageSize: filters.pageSize,
              totalRecords: totalCount,
              totalPages,
              hasNextPage: page < totalPages,
              hasPreviousPage: page > 1,
            }}
            loading={loading}
            onPageChange={(nextPage) => setFilter('page', nextPage, { resetPage: false })}
          />
        </>
      )}
    </section>
  );
};

export default PaymentsDuePanel;
